import type { ReactNode, FC } from 'react';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, FeedSortOrderSelectBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import {
  DndSortable,
  DndSortableChildrenProps,
  DndSortableElement,
} from '@wix/da-shared-react/pkg/Dnd/DndSortable';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { usePageOffsets } from '../../../../hooks/usePageOffsets';
import {
  getIsDndReorderSupported,
  getSelectedFolder,
} from '../../../../selectors/studio';
import { reorderMulti } from '../../../../actions/studio';
import StudioTableDragLayer from '../../../StudioTableDragLayer';
import BaseList from '../BaseList';
import { Props as StudioDeviationListProps } from '../../StudioDeviationList';

import s from './SortableDeviationList.scss';

const DRAG_THROTTLE = 250;
const DRAG_TOP_ID = -1;

interface Props extends StudioDeviationListProps {
  listClassName?: string;
  itemClassName?: string;
  cellClassName?: string;
  renderItem: (props: {
    deviation: PapiDeviation;
    offset: number;
    sortableProps?: DndSortableChildrenProps;
    isDraggingOver?: boolean;
  }) => ReactNode;
}

const SortableDeviationList: FC<Props> = ({
  listClassName,
  deviations,
  selectProps: { selected },
  renderItem,
}) => {
  const logBiEvent = useBiLogger();
  const isDndReorderSupported = useSelector(getIsDndReorderSupported);
  const selectedFolder = useSelector(getSelectedFolder);
  const { topOfPage } = usePageOffsets(selectedFolder);
  const [dragPosition, setDragPosition] = useState<number | null>(null);

  const dispatchReorderMulti = useAction(reorderMulti);
  const onReorderMulti = useCallback(
    (
      folderId: number,
      deviationIds: { deviationId: number; position: number }[]
    ) => dispatchReorderMulti(folderId, deviationIds),
    [dispatchReorderMulti]
  );

  const onDragOver = useThrottle(
    (id: number, position: number) => {
      setDragPosition(position);
    },
    [setDragPosition],
    DRAG_THROTTLE,
    { leading: true, trailing: false }
  );
  const onDragEnd = useCallback(
    (id: number, initialPosition: number) => {
      const draggedDeviations =
        selected.length > 0
          ? deviations.filter((_, idx) => selected.includes(idx))
          : [deviations.find(item => item.deviationId === id)];

      setDragPosition(null);
      if (
        selectedFolder &&
        dragPosition !== null &&
        dragPosition !== initialPosition
      ) {
        draggedDeviations.forEach(deviation => {
          deviation &&
            logBiEvent(
              BiData<FeedSortOrderSelectBiEvent>({
                evid: 403,
                itemid: deviation.deviationId,
                typeid: getDeviationResourceType(deviation),
                sort_order: 'drag_and_drop',
              })
            );
        });
        onReorderMulti(
          selectedFolder.folderId,
          draggedDeviations.map((deviation, i) => ({
            deviationId: deviation?.deviationId || 0,
            position:
              topOfPage +
              i +
              (dragPosition < initialPosition
                ? dragPosition
                : dragPosition - 1),
          }))
        );
      }
    },
    [
      deviations,
      logBiEvent,
      setDragPosition,
      onReorderMulti,
      selected,
      selectedFolder,
      dragPosition,
      topOfPage,
    ]
  );

  // The table is fixed-layout, so the top bookend's cells
  // determine the layout of everything proceeding
  const renderTopBookend = useCallback(
    sortableProps => (
      <DndSortableElement
        key="top-bookend"
        dndType={DraggableType.STUDIO_TABLE_ROW}
        dndAcceptType={DraggableType.STUDIO_TABLE_ROW}
        id={DRAG_TOP_ID}
        position={0}
        {...sortableProps}
      >
        {opts => (
          <li
            ref={opts.ref}
            className={cn(
              s['list-bookend'],
              dragPosition === 0 && s['list-bookend-hilite']
            )}
          />
        )}
      </DndSortableElement>
    ),
    [dragPosition]
  );

  return isDndReorderSupported ? (
    <DndSortable onDragOver={onDragOver} onDragEnd={onDragEnd}>
      {sortableProps => (
        <>
          <StudioTableDragLayer />
          <BaseList
            className={listClassName}
            beforeItems={renderTopBookend(sortableProps)}
            results={deviations}
            renderItem={(deviation, idx) =>
              renderItem({
                deviation,
                offset: idx,
                sortableProps,
                isDraggingOver: dragPosition === idx + 1,
              })
            }
          />
        </>
      )}
    </DndSortable>
  ) : (
    <BaseList
      className={listClassName}
      results={deviations}
      renderItem={(deviation, idx) =>
        renderItem({
          deviation,
          offset: idx,
        })
      }
    />
  );
};
SortableDeviationList.displayName = 'SortableDeviationList';

export default SortableDeviationList;
